import { Trophy, Globe, FileText, BookOpen, Users, Target } from 'lucide-react';
import CountdownTimer from '../components/CountdownTimer';
import WebinarSignUpButton from '../components/buttons/WebinarSignUpButton';
import DemoSignUpButton from '../components/buttons/DemoSignUpButton';
import PrimaryButton from '../components/buttons/PrimaryButton';
import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';
import { PAGE_TITLES } from '../constants/pageTracking';
import { trackPageView } from '../utils/trackingUtils';


export default function BusinessChallenge() {
  // Track page view
  useEffect(() => {
    trackPageView('BUSINESS_CHALLENGE', 'business_challenge');
  }, []);

  // Track section views
  const trackSectionView = (sectionName: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'section_view', {
        page_title: PAGE_TITLES.BUSINESS_CHALLENGE.tracking,
        section_name: sectionName
      });
    }
  };

  // Section tracking with Intersection Observer
  useEffect(() => {
    const sections = [
      { id: 'bc-hero', name: 'Business Challenge Hero' },
      { id: 'bc-how-it-works', name: 'How It Works' },
      { id: 'bc-competition', name: 'Competition Details' },
      { id: 'bc-benefits', name: 'Benefits' },
      { id: 'bc-cta', name: 'Call to Action' }
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionName = sections.find(s => s.id === entry.target.id)?.name;
            if (sectionName) {
              trackSectionView(sectionName);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  // Track button clicks
  const handleButtonClick = (action: string, buttonLocation: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'button_click', {
        page_title: PAGE_TITLES.BUSINESS_CHALLENGE.tracking,
        button_action: action,
        button_location: buttonLocation
      });
    }
  };

  // const themes = [
  //   {
  //     key: 'marketing',
  //     title: 'Become a Marketing Star! 🌟',
  //     items: [
  //       'Create Your Own Brand Story',
  //       'Get Your First Real Followers',
  //       'Master TikTok & Instagram',
  //       'Make Customers Love You'
  //     ]
  //   },
  //   {
  //     key: 'financial',
  //     title: 'Master Business Finance! 💼',
  //     items: [
  //       'Read Financial Reports Like a CEO',
  //       'Make Smart Business Decisions',
  //       'Understand Revenue & Costs',
  //       'Grow Your Business Profits'
  //     ]
  //   },
  //   {
  //     key: 'budgeting',
  //     title: 'Level Up Your Money Game! 🎯',
  //     items: [
  //       'Create Winning Business Budgets',
  //       'Manage Cash Flow Like a Pro',
  //       'Plan for Business Growth',
  //       'Make Every Dollar Count'
  //     ]
  //   },
  //   {
  //     key: 'entrepreneurship',
  //     title: 'Launch Your Dream Business! 🚀',
  //     items: [
  //       'Turn Your Ideas Into Gold',
  //       'Build Something Awesome',
  //       'Sell What People Love',
  //       'Become Your Own Boss'
  //     ]
  //   }
  // ];

  const benefits = [
    {
      icon: <Trophy className="w-12 h-12 text-primary" />,
      title: "Why Participate?",
      description: "Students play and have fun, develop knowledge and skills and compete to reach the finals and win prizes. 🏆"
    },
    {
      icon: <Globe className="w-12 h-12 text-primary" />,
      title: "Global Opportunity",
      description: "The top 3 teams in the National Championship qualify for the World Championship and the pursuit of the world champion title! 🌎"
    },
    {
      icon: <FileText className="w-12 h-12 text-primary" />,
      title: "Access to Templates",
      description: "Subject and theme specific simulation templates tailored to different competence goals. 📈"
    }
  ];

  const steps = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Sign Up",
      description: "Register your team and get access to practice simulations and competition resources."
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Learn",
      description: "Access comprehensive business curriculum aligned with educational standards."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Compete",
      description: "Join bi-weekly qualifying rounds and compete against peers worldwide."
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: "Win",
      description: "Top performers advance to finals with chances to win prizes and recognition."
    }
  ];

  const competitionRules = [
    {
      title: "5 Qualifying Rounds",
      description: "Each month, from January to June teams tackle new challenges tied to PBL and submit their solutions."
    },
    {
      title: "Collaborate and Compete",
      description: "Teens work in small teams to build their ideas, debate strategies, and present solutions."
    },
    {
      title: "Global Online Finals (July 2025)",
      description: "The top 10 teams showcase their skills in a high-stakes, virtual championship!"
    }
  ];

  const qualificationDetails = [
    "Complete at least one challenge by June 20, 2025",
    "Participate in two PBL assignments per challenge",
    "Sharpen skills and prove your team's potential",
    "Top performers advance to the global finals"
  ];

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLES.BUSINESS_CHALLENGE.seo}</title>
        <link rel="canonical" href="https://homeschool.emploskills.com/business-challenge" />
        <meta 
          name="description" 
          content="Join our Virtual Business Challenge designed for homeschooling teens aged 14-18. Compete globally, learn business skills, and win prizes!" 
        />
        <meta 
          name="keywords" 
          content="business challenge, homeschool competition, virtual business, teen entrepreneurship, business simulation" 
        />
        
        {/* Open Graph */}
        <meta property="og:title" content={PAGE_TITLES.BUSINESS_CHALLENGE.seo} />
        <meta property="og:description" content="Join the Virtual Business Challenge. Choose between individual ($69) or group access ($149) for our global business competition." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://homeschool.emploskills.com/business-challenge" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalEvent",
            "name": "EmploSkills Virtual Business Challenge",
            "description": "A global business competition for homeschooling teens aged 14-18",
            "eventStatus": "https://schema.org/EventScheduled",
            "startDate": "2025-02-12",
            "endDate": "2025-07-01",
            "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
            "offers": [
              {
                "@type": "Offer",
                "price": "69",
                "priceCurrency": "USD",
                "name": "Individual Access"
              },
              {
                "@type": "Offer",
                "price": "149",
                "priceCurrency": "USD",
                "name": "Group Access"
              }
            ]
          })}
        </script>

        {/* Add tracking title */}
        <meta name="analytics-page-title" content={PAGE_TITLES.BUSINESS_CHALLENGE.tracking} />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section id="bc-hero" className="bg-primary text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Enter the Emplo Business Challenge
            </h1>
            <p className="text-xl max-w-3xl mx-auto">
              Designed for homeschooling teens aged 14–18, The Virtual Business Challenge is the perfect extracurricular activity to inspire your HomeSchooling teens and equip them with critical transferable skills for future work or studies.
            </p>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="bc-how-it-works" className="py-20 px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
              How The Challenge Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="space-y-12">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-start">
                    <div className="bg-primary p-3 rounded-lg text-white mr-4">
                      {step.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Competition Format</h3>
                <ul className="space-y-4">
                  {competitionRules.map((rule, index) => (
                    <li key={index}>
                      <div className="flex items-center mb-1">
                        <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                        <h4 className="font-semibold">{rule.title}</h4>
                      </div>
                      <p className="text-gray-600 ml-4">{rule.description}</p>
                    </li>
                  ))}
                </ul>
                
                <h3 className="text-2xl font-bold mt-6 mb-4">Qualification Details</h3>
                <ul className="space-y-3">
                  {qualificationDetails.map((detail, index) => (
                    <li key={index} className="flex items-center">
                      <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Competition Call-to-Action */}
        <section id="bc-competition" className="py-12 md:py-20 px-4 md:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">
              Next Qualifying Round Starting Soon!
            </h2>
            <div className="mb-12">
              <CountdownTimer />
            </div>
            
            {/* Webinar CTA Card */}
            <div className="bg-primary text-white p-8 rounded-xl mt-12">
              <h3 className="text-2xl font-bold mb-4">
                Join The Launch Event
              </h3>
              <p className="mb-6">
                Learn how the Virtual Business Challenge can transform your homeschooling experience. Join our next live session!
              </p>
              <div className="bg-white/10 p-4 rounded-lg mb-6">
                <div className="font-semibold mb-1">Next Webinar:</div>
                <div>Wednesday 13th of January at 2:00 PM EST</div>
              </div>
              <div className="w-full flex justify-center">
                <WebinarSignUpButton />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="bc-benefits" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    {benefit.title}
                  </h3>
                  <p>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Options Section */}
        <section id="bc-cta" className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Ready to Get Started?
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Competition Card */}
              <div className="bg-white p-8 rounded-xl shadow-lg transform transition-transform hover:scale-105">
                <div className="flex flex-col h-full items-center text-center">
                  <h3 className="text-2xl font-bold mb-4">Join the Competition</h3>
                  <p className="mb-6 flex-grow">
                    Ready to dive in? Compete against other students in our next qualifying round and showcase your business acumen!
                  </p>
                  <div className="mt-auto">
                    <PrimaryButton 
                      to="/orderBusinessChallenge" 
                      onClick={() => handleButtonClick('buy_access', 'business_challenge_cta')}
                    >
                      Buy Access
                    </PrimaryButton>
                  </div>
                </div>
              </div>

              {/* Demo Card */}
              <div className="bg-white p-8 rounded-xl shadow-lg transform transition-transform hover:scale-105">
                <div className="flex flex-col h-full items-center text-center">
                  <h3 className="text-2xl font-bold mb-4">Try Demo Account</h3>
                  <p className="mb-6 flex-grow">
                    Not sure yet? Test drive our platform with a demo account and experience the challenge firsthand.
                  </p>
                  <div className="mt-auto">
                    <DemoSignUpButton />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}